/**
 * Singleton, DOM-level owner of the Five9 ADT iframe.
 *
 * The iframe is created exactly once (on first import) and lives directly
 * under `document.body`. React components mount/unmount around it but never
 * own it: they call `mountInto(containerEl)` to move the iframe into their
 * wrapper, and on unmount they call `detach()` which moves it back under
 * `document.body` (and hides it). Because we use `appendChild` on an
 * existing element, the browser MOVES the node rather than recreating it —
 * so the iframe's `contentWindow`, in-flight Five9 session, and any active
 * call all survive.
 *
 * This defends against any upstream cause (auth-guard flicker, Suspense
 * boundary, layout swap, etc.) that would otherwise unmount
 * `Five9CrmAdapter` and cause the iframe to fully reload.
 */

// f9verticalthreshold: below this iframe width the ADT uses its responsive
// vertical layout (fills the iframe); at/above it, a fixed ~200px centered
// column. Our docked panel is 320px wide, so keep the threshold above that.
const DEFAULT_FIVE9_IFRAME_SRC = 'https://app.five9.com/clients/integrations/adt.main.html?f9crmapi=true&f9verticalthreshold=500px';

// The ADT iframe URL is environment-configurable via
// `REACT_APP_FIVE9_ADT_URL` (substituted by CRA at build time). Falls
// back to the well-known production URL when unset so existing
// deployments keep working without a config change. Keep this in sync
// with the SDK loader (`REACT_APP_FIVE9_SDK_URL`) — they come from the
// same Five9 environment.
const FIVE9_IFRAME_SRC = process.env.REACT_APP_FIVE9_ADT_URL || DEFAULT_FIVE9_IFRAME_SRC;

// Diagnostic: log every module (re)evaluation. In a healthy CRA/webpack
// build this should fire EXACTLY ONCE per browser tab; if it fires again
// during a route change, something is triggering a full page reload
// (e.g. `window.location.href = '...'` from an interceptor).
// eslint-disable-next-line no-console
console.info('[Five9] persistentFive9Iframe module evaluated at', new Date().toISOString(), 'href=', typeof window !== 'undefined' ? window.location.href : 'no-window');

/** @type {HTMLIFrameElement|null} */
let iframeEl = null;
/** @type {HTMLElement|null} */
let attachedContainer = null;
/** Counter of full iframe `load` events; useful in devtools to spot
 *  unexpected re-loads of the singleton. @type {number} */
let loadCount = 0;

/**
 * Lazily create and park the singleton iframe under `document.body`.
 * Called by `mountInto(...)` on first use.
 *
 * @returns {HTMLIFrameElement|null}
 */
const ensureCreated = () => {
  if (iframeEl || typeof document === 'undefined') return iframeEl;
  iframeEl = document.createElement('iframe');
  iframeEl.title = 'Five9 ADT';
  iframeEl.setAttribute('allow', 'microphone; autoplay; clipboard-read; clipboard-write');
  iframeEl.setAttribute('draggable', 'false');
  iframeEl.setAttribute('data-five9-adt', 'singleton');
  // Fill whatever container we're placed into.
  iframeEl.style.display = 'block';
  iframeEl.style.position = 'absolute';
  iframeEl.style.inset = '0';
  iframeEl.style.height = '100%';
  iframeEl.style.width = '100%';
  iframeEl.style.border = 'none';
  iframeEl.style.pointerEvents = 'auto';
  iframeEl.style.zIndex = '1';
  // Park it off-screen under body until a container claims it, so the
  // Five9 session is already loading before the first mount.
  iframeEl.style.visibility = 'hidden';
  // Diagnostic: count every full iframe load (initial + any reloads). If
  // this fires more than once for a single browser session, the iframe's
  // src was reassigned or the DOM node was recreated.
  iframeEl.addEventListener('load', () => {
    loadCount += 1;
    // eslint-disable-next-line no-console
    console.info(`[Five9] iframe load #${loadCount} at`, new Date().toISOString());
  });
  // Assign src LAST so the load listener catches the initial load.
  iframeEl.src = FIVE9_IFRAME_SRC;
  document.body.appendChild(iframeEl);
  return iframeEl;
};

// Create lazily on first `mountInto(...)` — App.js imports this module
// unconditionally, but the master `five9Integration` feature gate may
// choose never to mount `Five9CrmAdapter`. In that case the iframe DOM
// node is never created, no Five9 session is opened, and no telemetry
// leaves the browser.

/**
 * Move the singleton iframe into the given container element. Safe to call
 * repeatedly with the same container. Creates the iframe DOM node on
 * first call.
 *
 * @param {HTMLElement} containerEl
 * @returns {void}
 */
export function mountInto(containerEl) {
  const el = ensureCreated();
  if (!el || !containerEl) return;
  if (attachedContainer === containerEl && el.parentNode === containerEl) return;
  containerEl.appendChild(el); // moves, does not recreate
  attachedContainer = containerEl;
  el.style.visibility = 'visible';
}

/**
 * Return the iframe to a hidden park under `document.body`. Called by
 * `Five9CrmAdapter` on unmount so the iframe survives outside the React
 * tree until the adapter re-mounts.
 *
 * @returns {void}
 */
export function detach() {
  // No `ensureCreated` here — if the iframe was never created (master
  // Five9 gate off, or first mount hasn't happened yet), there is
  // nothing to hide.
  if (!iframeEl) return;
  iframeEl.style.visibility = 'hidden';
  if (iframeEl.parentNode !== document.body) {
    document.body.appendChild(iframeEl);
  }
  attachedContainer = null;
}

/**
 * Read-only accessor for the singleton element, for callers that need to
 * inspect it (tests / diagnostics).
 *
 * @returns {HTMLIFrameElement|null}
 */
export function getIframeElement() {
  return iframeEl;
}

/**
 * Fully destroy the singleton iframe so the NEXT `mountInto(...)` creates
 * a fresh one. Called on auth transitions (logout / user switch) so the
 * previous user's ADT DOM instance and in-memory SDK state are torn down
 * before a subsequent authenticated session can adopt it.
 *
 * Contrast with `detach()`, which merely parks the iframe hidden under
 * `document.body` and preserves the session on purpose.
 *
 * @returns {void}
 */
export function reset() {
  if (!iframeEl) return;
  try {
    if (iframeEl.parentNode) iframeEl.parentNode.removeChild(iframeEl);
  } catch (_) { /* noop */ }
  iframeEl = null;
  attachedContainer = null;
  loadCount = 0;
}

